/**
 * Admin → Pipeline Prompt editor (§27.6).
 *
 * Route: /admin/prompts/:agentId/edit
 *
 * Loads the prompt markdown from S3 via the admin API, presents it in a
 * full-height textarea (monospace), and saves back to S3 on submit.
 * The pipeline worker reads prompts from S3 at execution time, so
 * changes take effect on the next pipeline run with no redeploy.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getAdminPrompt, updateAdminPrompt } from "../services/admin.js";
import ErrorBubble from "../components/ErrorBubble.jsx";
import LoadingSkeleton from "../components/LoadingSkeleton.jsx";

export default function AdminPromptEdit() {
  const { agentId } = useParams();
  const numericId = Number(agentId);

  const [content, setContent] = useState("");
  const [agentMeta, setAgentMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getAdminPrompt(numericId)
      .then((data) => {
        if (cancelled) return;
        const p = data.prompt;
        setContent(p.content || "");
        setAgentMeta(p);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err.message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [numericId]);

  async function handleSave() {
    setSaveError(null);
    setSaved(false);
    if (!content.trim()) {
      setSaveError("Prompt content cannot be empty.");
      return;
    }
    setSaving(true);
    try {
      await updateAdminPrompt(numericId, content);
      setSaved(true);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const isReadOnly = agentMeta && agentMeta.editable === false;
  const title = agentMeta
    ? `${isReadOnly ? "View" : "Edit"}: ${agentMeta.name || agentMeta.agentId}`
    : `Edit prompt ${agentId}`;

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <h1>{title}</h1>
        <Link to="/admin/prompts" className="btn btn-ghost">
          Back
        </Link>
      </div>

      {loadError && <ErrorBubble message={loadError} />}
      {saveError && <ErrorBubble message={saveError} />}

      {loading ? (
        <LoadingSkeleton variant="line" />
      ) : (
        <>
          {agentMeta && agentMeta.source === "tenant_s3" && (
            <p className="admin-page__hint">
              File: <code>{agentMeta.filename}</code> in{" "}
              <code>s3://ai-coach-pipeline/prompts/</code>. Changes take
              effect on the next pipeline run.
            </p>
          )}
          {agentMeta && agentMeta.source === "platform_baseline" && (
            <div className="admin-page__hint admin-page__hint--info">
              <strong>Read-only — platform baseline.</strong> This file is
              version-controlled and shared across every <code>baselineKey</code>{" "}
              tenant. Edit{" "}
              <code>pipeline/prompts/platform/{agentMeta.filename}</code> in
              the repo and open a PR — the change propagates on the next
              pipeline-worker Lambda rebuild. The pipeline renders this
              template against each tenant&apos;s <code>TenantProfile</code> +{" "}
              <code>ProductCatalogue</code> at runtime, so per-tenant
              customisation lives in the <code>/setup</code> catalogue, not here.
            </div>
          )}
          <textarea
            className="admin-json-editor"
            value={content}
            onChange={(e) => {
              if (isReadOnly) return;
              setContent(e.target.value);
              setSaved(false);
            }}
            readOnly={isReadOnly}
            spellCheck={false}
            rows={30}
          />
          {!isReadOnly && (
            <div className="admin-page__actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save"}
              </button>
              {saved && (
                <span className="admin-page__saved">Saved</span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
